import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import AuthToken from 'utils/localStorage/AuthToken';
import resourceUrl from 'utils/resourceUrl';
import formattedDate from 'utils/formattedDate';
import useOrganizationAdmin from 'utils/useOrganizationAdmin';

import Widget from './Widget';

import EventIcon from '@mui/icons-material/Event';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import { t, i18n } from 'i18n/index';

const CURRENCY_SYMBOLS = {
  'GBP': '£',
  'EUR': '€',
  'USD': '$',
  'BRL': 'R$',
  'CHF': 'CHF'
};

const PHASES = {
  EVENT_CREATION: 'event_creation',
  TICKET_PROMPT: 'ticket_prompt',
  TICKET_CREATION: 'ticket_creation',
  COMPLETE: 'complete'
};

const createDurationFormatter = (t) => (value) => {
  const hours = parseFloat(value);
  if (hours === 1) return t('aiChat:formatters:hour');
  if (hours < 1) return t('aiChat:formatters:minutes', { count: Math.round(hours * 60) });
  return t('aiChat:formatters:hours', { count: hours });
};

const createCapacityFormatter = (t) => (value) => t('aiChat:formatters:attendees', { count: value });

const createPriceFormatter = (t, currency = 'USD') => (value) => {
  if (!value || value === 0) return t('aiChat:formatters:free');
  const amount = (parseInt(value, 10) / 100).toFixed(2);
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${amount}`;
};

/**
 * AIEventCreator - Orchestrated multi-phase event + ticket creation
 *
 * Phases:
 * 1. event_creation - Gather event info, show event confirmation card
 * 2. ticket_prompt - Ask if user wants to add tickets (hardcoded message)
 * 3. ticket_creation - Gather ticket info, show ticket confirmation card (loops)
 * 4. complete - Done, redirect to event edit page
 */
const AIEventCreator = ({ onComplete }) => {
  const { organizationId } = useParams();
  const organization = useOrganizationAdmin();
  const currency = organization?.currency || 'USD';
  
  const [phase, setPhase] = useState(PHASES.EVENT_CREATION);
  const [eventId, setEventId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [entityData, setEntityData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [chatLanguage, setChatLanguage] = useState(null);
  
  const isMountedRef = useRef(true);
  const completionTimeoutRef = useRef(null);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
    };
  }, []);

  // Create chatT wrapper function that uses chatLanguage or falls back to i18n.language
  const chatT = useCallback((key, options) => {
    const language = chatLanguage || i18n.language;
    return i18n.getFixedT(language)(key, options);
  }, [chatLanguage]);
  
  const EVENT_CONFIG = useMemo(() => ({
    entityType: 'event',
    title: chatT('aiChat:eventCreator:title'),
    placeholder: chatT('aiChat:eventCreator:placeholder'),
    collapsedPlaceholder: chatT('aiChat:eventCreator:collapsedPlaceholder'),
    dataKey: 'event_data',
    supportsImageUpload: true,
    summaryFields: [
      {
        key: 'title',
        dataKey: 'title',
        icon: EventIcon,
        iconColor: 'var(--highlightColor)',
        valueClassName: 'summary-value-title',
        fieldClassName: 'summary-field-title',
        formatter: null
      },
      {
        key: 'start_date',
        dataKey: 'start_date',
        label: chatT('aiChat:labels:start'),
        formatter: (value) => formattedDate(value, 'datetime')
      },
      {
        key: 'duration_hours',
        dataKey: 'duration_hours',
        label: chatT('aiChat:labels:duration'),
        formatter: createDurationFormatter(chatT)
      },
      {
        key: 'location',
        dataKey: 'location',
        label: chatT('aiChat:labels:location'),
        formatter: null
      },
      {
        key: 'capacity',
        dataKey: 'capacity',
        label: chatT('aiChat:labels:capacity'),
        formatter: createCapacityFormatter(chatT)
      },
      {
        key: 'description',
        dataKey: 'description',
        label: chatT('aiChat:labels:description'),
        formatter: null
      }
    ]
  }), [chatT]);

  const TICKET_CONFIG = useMemo(() => ({
    entityType: 'ticket',
    title: chatT('aiChat:ticketCreator:title'),
    placeholder: chatT('aiChat:ticketCreator:placeholder'),
    collapsedPlaceholder: chatT('aiChat:ticketCreator:collapsedPlaceholder'),
    dataKey: 'ticket_data',
    supportsImageUpload: false,
    summaryFields: [
      {
        key: 'title',
        dataKey: 'title',
        icon: ConfirmationNumberIcon,
        iconColor: 'var(--highlightColor)',
        valueClassName: 'summary-value-title',
        fieldClassName: 'summary-field-title',
        formatter: null
      },
      {
        key: 'price',
        dataKey: 'price',
        label: chatT('aiChat:labels:price'),
        formatter: createPriceFormatter(chatT, currency)
      },
      {
        key: 'description',
        dataKey: 'description',
        label: chatT('aiChat:labels:description'),
        formatter: null
      }
    ]
  }), [chatT, currency]);

  const INITIAL_EVENT_MESSAGE = useMemo(() => ({
    role: 'assistant',
    content: t('aiChat:eventCreator:initialMessage')
  }), [t]);

  const getCurrentConfig = useCallback(() => {
    if (phase === PHASES.TICKET_CREATION) {
      return TICKET_CONFIG;
    }
    return EVENT_CONFIG;
  }, [phase, TICKET_CONFIG, EVENT_CONFIG]);

  const getInitialMessage = useCallback(() => {
    return INITIAL_EVENT_MESSAGE;
  }, [INITIAL_EVENT_MESSAGE]);

  const filterSystemMessages = (msgs) => msgs.filter(m => m.role !== 'system').slice(-10);

  const sendChatMessage = useCallback(async (message, chatHistory) => {
    const url = resourceUrl(
      '/organizations/:organization_id/ai_event_creator/chat',
      '',
      { organization_id: organizationId }
    );
    const authToken = AuthToken.read();

    const body = {
      message,
      history: filterSystemMessages(chatHistory),
      phase,
      ...(eventId && { event_id: eventId })
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'X-Locale': chatLanguage || i18n.language || 'en'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || chatT('aiChat:errors:failedToSend'));
    }

    return response.json();
  }, [organizationId, phase, eventId, chatLanguage, chatT, i18n]);

  const createEntity = useCallback(async (data, imageFile = null) => {
    const url = resourceUrl(
      '/organizations/:organization_id/ai_event_creator/create',
      '',
      { organization_id: organizationId }
    );
    const authToken = AuthToken.read();

    const formData = new FormData();
    const currentConfig = getCurrentConfig();

    formData.append('phase', phase);
    if (eventId) {
      formData.append('event_id', eventId);
    }

    const dataWithImage = imageFile && currentConfig.supportsImageUpload
      ? { ...data, image: imageFile, image_filename: imageFile.name }
      : data;

    const isArray = Array.isArray(dataWithImage);
    const items = isArray ? dataWithImage : [dataWithImage];

    items.forEach((item, index) => {
      Object.keys(item).forEach(key => {
        const value = item[key];
        if (value !== null && value !== undefined && value !== '') {
          const formKey = isArray 
            ? `${currentConfig.dataKey}[${index}][${key}]`
            : `${currentConfig.dataKey}[${key}]`;
          formData.append(formKey, value);
        }
      });
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'X-Locale': chatLanguage || i18n.language || 'en'
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const entityTypeName = phase === PHASES.EVENT_CREATION ? 'event' : 'ticket';
      throw new Error(errorData.error || chatT('aiChat:errors:failedToCreate', { entityType: entityTypeName }));
    }

    return response.json();
  }, [organizationId, phase, eventId, getCurrentConfig, chatLanguage, chatT, i18n]);

  const handleSendMessage = useCallback(async (message) => {
    const userMessage = { role: 'user', content: message };

    if (entityData) {
      setEntityData(null);
    }

    const initialMsg = getInitialMessage();
    const newMessages = messages.length === 0
      ? [initialMsg, userMessage]
      : [...messages, userMessage];
    setMessages(newMessages);

    setIsLoading(true);
    setError(null);

    try {
      const result = await sendChatMessage(message, newMessages);

      if (!chatLanguage && result.detected_language) {
        setChatLanguage(result.detected_language);
      }

      const assistantMessage = { role: 'assistant', content: result.message };
      setMessages(prev => [...prev, assistantMessage]);

      if (result.phase && result.phase !== phase) {
        setPhase(result.phase);

        if (result.event_id) {
          setEventId(result.event_id);
        }

        if (result.phase === PHASES.COMPLETE) {
          if (completionTimeoutRef.current) {
            clearTimeout(completionTimeoutRef.current);
          }
          completionTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              onComplete(result.event_id || eventId);
            }
            completionTimeoutRef.current = null;
          }, 4000); // 4 second delay
          return;
        }
      }

      const currentConfig = getCurrentConfig();
      if (result.ready && result[currentConfig.dataKey]) {
        setEntityData(result[currentConfig.dataKey]);
      }
    } catch (err) {
      setError(err.message || chatT('aiChat:errors:somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }, [messages, phase, eventId, entityData, sendChatMessage, getCurrentConfig, getInitialMessage, onComplete, chatLanguage, chatT]);

  const handleConfirmCreate = useCallback(async (data, imageFile = null) => {
    setIsCreating(true);
    setError(null);

    try {
      const result = await createEntity(data, imageFile);

      setEntityData(null);

      if (result.message) {
        setMessages(prev => [...prev, { role: 'assistant', content: result.message }]);
      }

      if (result.phase) {
        setPhase(result.phase);
      }

      if (result.event_id) {
        setEventId(result.event_id);
      }

      if (result.phase === PHASES.COMPLETE) {
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
        }
        completionTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            onComplete(result.event_id || eventId);
          }
          completionTimeoutRef.current = null;
        }, 5000); // 5 second delay
      }
    } catch (err) {
      setError(err.message || chatT('aiChat:errors:somethingWentWrong'));
    } finally {
      setIsCreating(false);
    }
  }, [createEntity, eventId, onComplete, chatT]);

  const handleMakeChanges = () => {
    setEntityData(null);
  };

  // Handle reset
  const handleReset = () => {
    // Clear any pending completion timeout to prevent navigation after reset
    if (completionTimeoutRef.current) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    setMessages([]);
    setEntityData(null);
    setError(null);
    setIsLoading(false);
    setPhase(PHASES.EVENT_CREATION);
    setEventId(null);
    setChatLanguage(null);
  };

  // Handle dismiss error
  const handleDismissError = () => {
    setError(null);
  };

  // Get display messages
  const currentConfig = getCurrentConfig();
  const initialMsg = getInitialMessage();
  const displayMessages = messages.length === 0 ? [initialMsg] : messages;

  return (
    <Widget
      config={{
        entityType: currentConfig.entityType,
        title: currentConfig.title,
        placeholder: currentConfig.placeholder,
        collapsedPlaceholder: currentConfig.collapsedPlaceholder,
        summaryFields: currentConfig.summaryFields,
        supportsImageUpload: currentConfig.supportsImageUpload
      }}
      chat={{
        messages: displayMessages,
        isLoading,
        error
      }}
      entity={{
        data: entityData,
        isCreating
      }}
      handlers={{
        onSendMessage: handleSendMessage,
        onReset: handleReset,
        onConfirmCreate: handleConfirmCreate,
        onMakeChanges: handleMakeChanges,
        onDismissError: handleDismissError
      }}
      chatT={chatT}
    />
  );
};

AIEventCreator.propTypes = {
  onComplete: PropTypes.func.isRequired
};

export default AIEventCreator;
